<template>
  <page-loader>
    <v-container>
      <v-row>
        <v-col>
          <filter-accordion
            :loading="isLoading"
            :query-count="$_queryHandler_numberOfQueries"
            :query-changed="$_queryHandler_hasQueryChanged"
            @clear-filters="$_queryHandler_resetFilters"
          >
            <template #search>
              <search-input v-model="query.searchTerm" hide-details outlined clearable />
            </template>

            <template #content>
              <data-table
                :headers="headers"
                :items="modules"
                :route="{ name: 'modules.edit', params: ['id'] }"
                :items-per-page="pagination.perPage"
                :page="pagination.currentPage"
                :server-items-length="pagination.total"
                :footer-props="{ 'items-per-page-options': perPageOptions }"
                disable-sort
                @pagination="$_queryHandler_onUpdatePagination"
              >
                <template #header.usage>
                  <v-tooltip top>
                    <template #activator="{ on }">
                      <div class="d-inline-flex align-center">
                        <span>Module Usage</span>
                        <v-icon right small v-on="on">info</v-icon>
                      </div>
                    </template>
                    <span>Number of Page Types using module</span>
                  </v-tooltip>
                </template>
                <template #item.usage="{ item }">
                  <base-chip :text="String(getPageTypesUsingModule(item.id))" width="140" small />
                </template>
              </data-table>
            </template>
          </filter-accordion>
        </v-col>
      </v-row>
    </v-container>

    <fab-create v-if="isPermittedTo('create')" type="Module" @click="showCreateModal = true" />

    <!-- Create Modal -->
    <generic-modal
      v-model="showCreateModal"
      :is-disabled="isLoading || !newModule"
      :is-loading="isLoading"
      title="Create Module"
      persistent
      hide-louis
      @modal-confirm="onCreateConfirm"
      @modal-cancel="onCreateCancel"
    >
      <v-form ref="form">
        <text-input v-model="newModule" label="Title" hide-details="auto" required />
      </v-form>
    </generic-modal>
  </page-loader>
</template>

<script>
// Services
import { modules } from '@/services/offers'
// Helpers
import { mapActions, mapGetters, mapMutations } from 'vuex'
import { upperFirst, camelCase } from 'lodash-es'
import isSet from '@/utils/is-set'
// Mixins
import QueryParamsHandler from '@/mixins/query-params-handler'
import PermissionsHandler from '@/mixins/permissions-handler'
// Components
import BaseChip from '@/components/chips/base-chip'
import DataTable from '@/components/data-table'
import FabCreate from '@/components/fab-create'
import FilterAccordion from '@/components/filter-accordion'
import GenericModal from '@/components/modals/generic-modal'
import PageLoader from '@/components/page-loader'
import SearchInput from '@/components/forms/search-input'
import TextInput from '@/components/forms/text-input'

const newBlankModule = () => ({
  name: undefined,
  title: undefined,
  fields: [],
})

export default {
  name: 'Module',
  components: {
    BaseChip,
    DataTable,
    FabCreate,
    FilterAccordion,
    GenericModal,
    PageLoader,
    SearchInput,
    TextInput,
  },
  mixins: [QueryParamsHandler, PermissionsHandler],
  data: () => ({
    headers: [
      { text: 'Title', value: 'title' },
      { text: 'Module Usage', value: 'usage', align: 'center' },
    ],
    modules: [],
    newModule: '',
    isLoading: false,
    pagination: { perPage: 15 },
    perPageOptions: [15, 30, 50, 100],
    queriesDefined: [],
    showCreateModal: false,
  }),
  computed: {
    ...mapGetters({
      pageTypes: 'pageTypes/getPageTypes',
    }),
  },
  async created() {
    await Promise.all([this.getModules(), this.getPageTypes({ paginate: { perPage: null } })])
    this.setPageLoading(false)
  },
  methods: {
    ...mapActions({ getPageTypes: 'pageTypes/fetch' }),
    ...mapMutations({
      setPageLoading: 'general/setPageLoading',
    }),
    async getModules() {
      this.isLoading = true

      try {
        const { params, searchTerm: search } = this.query
        const { perPage, currentPage: page } = this.pagination
        const filters = Object.entries(params).reduce((acc, [key, value]) => {
          if (isSet(value)) acc[key] = value
          return acc
        }, {})
        const { data, pagination: { total } = {} } = await modules.getAll({ search, filters, page, perPage })
        this.modules = data
        this.pagination.total = total
      } catch (error) {
        this.$notify({
          type: 'error',
          text: 'Unable to retrieve Modules',
          data: { error },
        })
      }
      this.isLoading = false
    },
    getPageTypesUsingModule(moduleId) {
      return this.pageTypes.data.filter(({ schema: { sections } }) =>
        Object.values(sections)
          .flat()
          .find(({ id }) => moduleId === id)
      ).length
    },
    async handleQueryUpdate() {
      await this.getModules()
    },
    onCreateCancel() {
      this.showCreateModal = false
      this.newModule = ''
    },
    async onCreateConfirm() {
      try {
        if (!this.$refs.form.validate()) return

        this.isLoading = true

        const { id } = await modules.createModule({
          ...newBlankModule(),
          title: this.newModule,
          name: upperFirst(camelCase(this.newModule)),
        })
        this.$notify({
          type: 'success',
          text: 'Successfully created Module',
        })
        this.$router.push({ name: 'modules.edit', params: { id } })
      } catch (error) {
        this.$notify({
          type: 'error',
          text: 'Unable to create Module',
          data: { error },
          duration: -1,
        })
      }
      this.isLoading = false
    },
  },
}
</script>
